import { captureException } from "@/lib/monitoring";

/**
 * Errors while the app boots (Sentry issue 7776580584): the entrypoint only
 * caught startup failures on pages without `#app`; on every Inertia page the
 * error became an unhandled rejection and the page stayed empty. This module
 * bundles the response: report the error, then reload once so the browser
 * picks up fresh assets instead of leaving the user on an empty page.
 */

/**
 * sessionStorage-shaped seam, for tests only: the browser mode uses
 * `window.sessionStorage`, tests plug in a map.
 */
export type StorageLike = {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
};

export type StartupRecoveryOptions = {
  storage: StorageLike | null;
  reload(): void;
};

const RELOAD_MARK_KEY = "startupReloadMark";

/**
 * Gives the Sentry transport a beat to start its async send before the
 * navigation of the reload cancels in-flight requests, so the very error
 * this module exists to report survives the unload.
 */
const RELOAD_DELAY_MS = 100;

/**
 * The `window.sessionStorage` property access itself throws in browsers with
 * all cookies blocked, and is missing under SSR. Returns null in both cases;
 * callers then skip the reload guard and only report the error.
 */
export function safeSessionStorage(): StorageLike | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * sessionStorage throws in private mode or with blocked cookies. Without a
 * mark, a reload would loop when the failure is permanent, so on any storage
 * failure the error is reported without a reload.
 */
function markSession(storage: StorageLike): boolean {
  try {
    storage.setItem(RELOAD_MARK_KEY, "1");
    return true;
  } catch {
    return false;
  }
}

/**
 * Reports the error with startup context to Sentry, then reloads once when
 * the session is not yet marked and the storage accepted the mark. Without a
 * reload the error is logged to the console, keeping the failure visible in
 * environments without a Sentry DSN. The caller wires in the real seams.
 */
export function recoverFromStartupFailure(
  error: unknown,
  { storage, reload }: StartupRecoveryOptions,
): void {
  let reloaded = false;

  if (storage && storage.getItem(RELOAD_MARK_KEY) !== "1") {
    reloaded = markSession(storage);
  }

  captureException(error, { scope: "startup", reloaded });

  if (reloaded) {
    setTimeout(() => reload(), RELOAD_DELAY_MS);
  } else {
    console.error(error);
  }
}

/**
 * Removes the session mark so a later failure may reload again (a successful
 * load does not clear it on its own). Fails silently.
 */
export function clearStartupReloadMark({ storage }: { storage: StorageLike | null }): void {
  if (!storage) {
    return;
  }

  try {
    storage.removeItem(RELOAD_MARK_KEY);
  } catch {
    // Blocked storage: the mark stays and keeps the loop guard active.
  }
}
